/**
 * Simple Builder JSON Schema
 *
 * This schema defines a simplified JSON structure that AI can easily generate.
 * It gets transformed into the complex Builder JSON format programmatically.
 *
 * Design principles:
 * 1. Key names indicate block types (no nested type fields)
 * 2. Flat structure where possible
 * 3. Canonical rich inline arrays for text content
 * 4. Minimal nesting depth
 * 5. AI focuses on CONTENT, transformer handles STRUCTURE
 */

// ============================================================================
// TEXT CONTENT
// ============================================================================

export type SimpleAlignment = 'left' | 'center' | 'right' | 'justify';

export interface SimpleTextLeaf {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  fontSize?: string | number;
}

export interface SimpleTextVariable {
  var: string;
  /** The text the user typed over the variable's own value, for this one mention. */
  override?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  fontSize?: string | number;
}

export interface SimpleTextLink {
  link: string;
  newTab?: boolean;
  content: SimpleTextContent;
}

export type SimpleTextNode = SimpleTextLeaf | SimpleTextVariable | SimpleTextLink;
export type SimpleTextContent = SimpleTextNode[];

export type SimpleInlineFieldType =
  | 'text_input'
  | 'name'
  | 'email'
  | 'date'
  | 'number'
  | 'checkbox'
  | 'dropdown'
  | 'textarea';

export interface SimpleFillableFieldOption {
  label: string;
  value: string;
}

export interface SimpleFillableFieldNode {
  field: {
    type: SimpleInlineFieldType;
    placeholder?: string;
    required?: boolean;
    options?: SimpleFillableFieldOption[];
    multiSelect?: boolean;
    min?: string | number;
    max?: string | number;
    rows?: number;
    maxLength?: number;
  };
}

export type SimpleFillableTextNode =
  | SimpleTextLeaf
  | SimpleTextVariable
  | SimpleTextLink
  | SimpleFillableFieldNode;
export type SimpleFillableTextContent = SimpleFillableTextNode[];

export interface SimpleSlateVariable {
  key: string;
  value?: string;
  type?: 'system' | 'custom';
  isOverride?: boolean;
}

export interface SimpleSlateDropdownOption {
  label: string;
  value: string;
}

export interface SimpleSlateTextNode {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  color?: string;
  fontSize?: string | number;
}

export interface SimpleSlateElementNode {
  type: string;
  children: SimpleSlateNode[];
  url?: string;
  newTab?: boolean;
  align?: SimpleAlignment;
  listStyle?: string;
  textStyle?: SimpleParagraphTextStyle;
  indentLevel?: number;
  restartNumbering?: boolean;
  listVariant?: string;
  character?: string;
  variable?: SimpleSlateVariable;
  fieldType?: string;
  fieldId?: string;
  placeholder?: string;
  value?: string;
  required?: boolean;
  options?: SimpleSlateDropdownOption[];
  multiSelect?: boolean;
  min?: string | number;
  max?: string | number;
  data?: Record<string, unknown>;
}

export type SimpleSlateNode = SimpleSlateTextNode | SimpleSlateElementNode;
export type SimpleRichContent = SimpleSlateNode[];

/** The editor's "Body text" and "Small text" paragraph styles. */
export type SimpleParagraphTextStyle = 'body-text' | 'small-text';

/** Optional style properties for text blocks */
export interface SimpleTextStyle {
  fontSize?: string; // e.g., "72px" for large title headings
  color?: string;
  backgroundColor?: string;
  align?: SimpleAlignment;
  listStyle?: string;
  textStyle?: SimpleParagraphTextStyle;
}

/**
 * A list item is its inline content. The object form is only used when the item carries
 * indentation, a numbering restart, its own alignment or a non-default list-item variant.
 */
export type SimpleListItem<Content = SimpleTextContent> =
  | Content
  | {
      content: Content;
      indent?: number;
      restart?: boolean;
      align?: SimpleAlignment;
      variant?: string;
    };

/** Text block - key indicates heading level or type */
export interface SimpleTextBlock {
  h1?: SimpleTextContent;
  h2?: SimpleTextContent;
  h3?: SimpleTextContent;
  h4?: SimpleTextContent;
  h5?: SimpleTextContent;
  p?: SimpleTextContent;
  bullets?: SimpleListItem[];
  numbered?: SimpleListItem[];
  todo?: SimpleListItem[];
  /** Style overrides (primarily for title page large headings) */
  style?: SimpleTextStyle;
}

export interface SimpleTextGroupBlock {
  text: SimpleTextChildItem[];
  style?: SimpleTextStyle;
}

export interface SimpleCommonBlockStyle {
  align?: SimpleAlignment;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  borderRadius?: number;
  width?: number | string;
  height?: number;
}

// ============================================================================
// MEDIA BLOCKS
// ============================================================================

/** Image block - can be URL string or object with details */
export interface SimpleImageValue {
  url?: string;
  alt?: string;
  prompt?: string; // AI image generation prompt
  image_search_query?: string;
  height?: number;
  width?: number | string;
  align?: SimpleAlignment;
  maskShape?: string;
  fitToWidth?: boolean;
  cropData?: Record<string, any>;
  focusPoint?: { x: number; y: number };
  caption?: string;
  colorCode?: string;
  image_generation_request_id?: string;
  image_generation_status?: string;
  image_generation_error?: string;
  style?: SimpleCommonBlockStyle;
}

export interface SimpleImageBlock {
  image:
    | string // Just a URL
    | SimpleImageValue;
}

/** Video block */
export type SimpleVideoDisplayMode = 'player' | 'card';

export interface SimpleVideoValue {
  src?: string;
  video?: string;
  platform?: string;
  title?: string;
  description?: string;
  align?: SimpleAlignment;
  height?: number;
  width?: number | string;
  displayMode?: SimpleVideoDisplayMode;
  previewThumbnail?: string;
  previewTitle?: string;
  previewDescription?: string;
  previewWebsite?: string;
  aspectRatio?: number;
  style?: SimpleCommonBlockStyle;
}

export interface SimpleVideoBlock {
  video: SimpleVideoValue;
}

export interface SimpleEmbedWebpageBlock {
  embed_webpage: SimpleVideoValue;
}

/** Button block */
export type SimpleButtonVariant = 'primary' | 'secondary' | 'outline';

export interface SimpleButtonStyle {
  align?: SimpleAlignment;
  width?: number;
  height?: number;
  borderRadius?: number;
  color?: string;
  backgroundColor?: string;
  borderColor?: string;
  borderWidth?: number;
  fontFamily?: string;
  fontWeight?: string | number;
}

export interface SimpleButtonItem {
  label: string;
  url?: string;
  variant?: SimpleButtonVariant;
  fullWidth?: boolean;
  style?: SimpleButtonStyle;
}

export interface SimpleButtonBlock {
  button:
    | SimpleButtonItem
    | {
        buttons: SimpleButtonItem[];
        fullWidth?: boolean;
        align?: SimpleAlignment;
      };
}

/** Icon block */
export interface SimpleIconBlock {
  icon:
    | string
    | {
        iconName?: string;
        iconSvg?: string;
        width?: number;
        height?: number;
        iconColor?: string;
        align?: SimpleAlignment;
        style?: SimpleCommonBlockStyle;
      };
}

/** Callout box block */
export type SimpleCalloutStyle = 'note' | 'info' | 'warning' | 'caution' | 'success' | 'question';

export interface SimpleCalloutBlock {
  callout_box:
    | string
    | {
        content: string | SimpleRichContent;
        calloutStyle?: SimpleCalloutStyle;
        hideIcon?: boolean;
        bgOpacity?: number;
        borderWidth?: number;
        customColor?: string;
      };
}

/** Divider block */
export type SimpleDividerStyle = 'solid' | 'dashed' | 'dotted';

export interface SimpleDividerBlock {
  divider:
    | true
    | {
        style?: SimpleDividerStyle;
        thickness?: number;
        color?: string;
        paddingTop?: number;
        paddingBottom?: number;
        label?: string;
        showLabel?: boolean;
      };
}

/** Label block */
export interface SimpleLabelItem {
  text: string;
  variant?: 'solid' | 'outline';
  icon?: string;
  style?: {
    backgroundColor?: string;
    color?: string;
    borderColor?: string;
  };
}

export interface SimpleLabelBlock {
  label:
    | string
    | string[]
    | {
        labels: (string | SimpleLabelItem)[];
        fullWidth?: boolean;
        align?: SimpleAlignment;
      };
}

/** Block quote block */
export interface SimpleBlockQuoteBlock {
  block_quote:
    | SimpleTextContent
    | {
        content: SimpleTextContent;
        style?: SimpleTextStyle;
      };
}

/** Code block - displays monospace panel with a copy button */
export interface SimpleCodeBlock {
  code:
    | string
    | string[]
    | SimpleTextContent
    | {
        content: string | string[] | SimpleTextContent;
        style?: SimpleTextStyle;
      };
}

export type SimpleTextChildItem = SimpleTextBlock | SimpleBlockQuoteBlock;
export type SimpleSmartTextItem = SimpleTextChildItem;
export type SimpleSmartTextContent = SimpleTextContent | SimpleSmartTextItem[];

export interface SimpleFillableChildTextBlock {
  h1?: SimpleFillableTextContent;
  h2?: SimpleFillableTextContent;
  h3?: SimpleFillableTextContent;
  h4?: SimpleFillableTextContent;
  h5?: SimpleFillableTextContent;
  p?: SimpleFillableTextContent;
  bullets?: SimpleListItem<SimpleFillableTextContent>[];
  numbered?: SimpleListItem<SimpleFillableTextContent>[];
  style?: SimpleTextStyle;
}

export interface SimpleFillableChildBlockQuote {
  block_quote:
    | SimpleFillableTextContent
    | {
        content: SimpleFillableTextContent;
        style?: SimpleTextStyle;
      };
}

export type SimpleFillableChildItem = SimpleFillableChildTextBlock | SimpleFillableChildBlockQuote;

// ============================================================================
// DATA BLOCKS
// ============================================================================

/** Chart dataset */
export interface SimpleChartDataset {
  label: string;
  data: number[];
  backgroundColor?: string | string[];
  borderColor?: string | string[];
  borderWidth?: number;
  fill?: boolean;
  tension?: number;
}

/** Chart types supported */
export type SimpleChartType =
  | 'bar'
  | 'column'
  | 'line'
  | 'pie'
  | 'donut'
  | 'area'
  | 'scatter'
  | 'radar'
  | 'gauge'
  | 'funnel'
  | 'treemap'
  | 'heatmap'
  | 'sankey'
  | 'histogram'
  | 'box'
  | 'violin';

/** Chart block */
export interface SimpleChartBlock {
  chart: {
    type: SimpleChartType;
    title?: string;
    labels: string[];
    datasets: SimpleChartDataset[];
    /** Size the user gave the chart on the page. */
    width?: number;
    height?: number;
    align?: SimpleAlignment;
    /** Chart settings with no field of their own: options, style and extra data keys. Kept as stored. */
    config?: Record<string, unknown>;
  };
}

export interface SimpleTableCellFormat {
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  backgroundColor?: string;
  align?: SimpleAlignment;
}

export interface SimpleTableCellImageData {
  image: string;
  alt?: string;
  height?: number;
  width?: string | number;
  style?: Record<string, string>;
  maskShape?: string;
  fitToWidth?: boolean;
  cropData?: Record<string, unknown>;
}

export interface SimpleTableCellVideoData {
  video: string;
  platform?: string;
  displayMode?: 'player' | 'card';
  previewThumbnail?: string;
  previewTitle?: string;
  previewDescription?: string;
  previewWebsite?: string;
  shouldCalculateAspectRatio?: boolean;
}

export interface SimpleTableCellButtonData {
  label: string;
  url: string;
  fullWidth?: boolean;
  variant?: 'primary' | 'secondary' | 'outline';
  style?: {
    align?: SimpleAlignment;
    color?: string;
    backgroundColor?: string;
    borderColor?: string;
  };
}

export type SimpleTableEmbeddedBlock =
  | {
      kind: 'table_cell_block';
      blockType: 'image';
      data: SimpleTableCellImageData;
    }
  | {
      kind: 'table_cell_block';
      blockType: 'video';
      data: SimpleTableCellVideoData;
    }
  | {
      kind: 'table_cell_block';
      blockType: 'button';
      data: SimpleTableCellButtonData;
    };

export type SimpleTableCellContent = string | SimpleRichContent | SimpleTableEmbeddedBlock;

export type SimpleTableCellValue =
  | string
  | SimpleRichContent
  | SimpleTableEmbeddedBlock
  | {
      content: SimpleTableCellContent;
      format?: SimpleTableCellFormat;
    };

export type SimpleTableRow = SimpleTableCellValue[];

/** Table block - supports shorthand string[][] and richer structured cells */
export interface SimpleTableBlock {
  table:
    | SimpleTableRow[]
    | {
        rows: SimpleTableRow[];
        column_widths?: number[];
        style?: SimpleCommonBlockStyle;
      };
}

/** Pricing table footer row for global adjustments */
export interface SimplePricingTableFooterRow {
  label: string;
  value: string;
  type: 'discount' | 'tax' | 'fee' | null;
  format: 'percentage' | 'currency' | null;
}

export type SimplePricingColumnRole =
  | 'item'
  | 'text'
  | 'unit_price'
  | 'quantity'
  | 'discount'
  | 'tax'
  | 'fee'
  | 'line_total';

export interface SimplePricingTableColumn {
  label: string;
  role: SimplePricingColumnRole;
  format: 'percentage' | 'currency' | null;
}

export interface SimpleSemanticPricingTable {
  columns: SimplePricingTableColumn[];
  rows: string[][];
  column_widths?: number[] | null;
  header_format?: SimpleTableCellFormat;
  row_cell_formats?: SimpleTableCellFormat[];
  column_cell_formats?: SimpleTableCellFormat[];
  footer_rows?: SimplePricingTableFooterRow[] | null;
}

export interface SimpleLegacyPricingTable {
  rows: SimpleTableRow[];
  column_types: ('discount' | 'tax' | 'fee' | null)[];
  column_formats: ('percentage' | 'currency' | null)[];
  column_widths?: number[];
  row_cell_formats?: SimpleTableCellFormat[];
  column_cell_formats?: SimpleTableCellFormat[];
  footer_rows?: SimplePricingTableFooterRow[] | null;
}

/** Pricing table block with semantic columns. Legacy positional input remains readable. */
export interface SimplePricingTableBlock {
  pricing_table: SimpleSemanticPricingTable | SimpleLegacyPricingTable;
}

/** Quotation builder block - interactive quotation/proposal builder */
export interface SimpleQuotationBuilderBlock {
  quotation_builder: true | Record<string, any>;
}

// ============================================================================
// SMART BLOCKS
// ============================================================================

/** Smart block item - used by all smart block types */
export interface SimpleSmartBlockItem {
  title: SimpleSmartTextContent;
  description: SimpleSmartTextContent;
  icon?: string;
  color?: string;
  bgColor?: string;
}

export interface SimpleSmartBlockStyle {
  color?: string;
}

export interface SimpleSmartBlockBaseConfig {
  items: SimpleSmartBlockItem[];
  layoutId?: string;
  style?: SimpleSmartBlockStyle;
}

export interface SimpleTimelineBlockConfig extends SimpleSmartBlockBaseConfig {
  viewMode?: 'vertical' | 'horizontal';
}

/** Timeline smart block */
export interface SimpleTimelineBlock {
  timeline: SimpleSmartBlockItem[] | SimpleTimelineBlockConfig;
}

/** Process flow smart block */
export interface SimpleProcessFlowBlockConfig extends SimpleSmartBlockBaseConfig {
  columns?: 'single' | 'two' | 'three';
}

export interface SimpleProcessFlowBlock {
  process_flow: SimpleSmartBlockItem[] | SimpleProcessFlowBlockConfig;
}

/** Steps smart block */
export interface SimpleStepsBlockConfig extends SimpleSmartBlockBaseConfig {}

export interface SimpleStepsBlock {
  steps: SimpleSmartBlockItem[] | SimpleStepsBlockConfig;
}

/** Arrow smart block (3 or 6 items) */
export interface SimpleArrowBlockConfig extends SimpleSmartBlockBaseConfig {
  direction?: 'right' | 'bottom';
}

export interface SimpleArrowBlock {
  arrow: SimpleSmartBlockItem[] | SimpleArrowBlockConfig;
}

/** Staircase smart block */
export interface SimpleStaircaseBlockConfig extends SimpleSmartBlockBaseConfig {}

export interface SimpleStaircaseBlock {
  staircase: SimpleSmartBlockItem[] | SimpleStaircaseBlockConfig;
}

/** Pyramid smart block */
export interface SimplePyramidBlockConfig extends SimpleSmartBlockBaseConfig {
  orientation?: 'up' | 'down';
  textInside?: boolean;
}

export interface SimplePyramidBlock {
  pyramid: SimpleSmartBlockItem[] | SimplePyramidBlockConfig;
}

/** Funnel smart block */
export interface SimpleFunnelBlock {
  funnel: SimpleSmartBlockItem[] | SimplePyramidBlockConfig;
}

/** Circular arrow smart block (the builder renders up to 8 items) */
export interface SimpleCircularArrowBlockConfig extends SimpleSmartBlockBaseConfig {}

export interface SimpleCircularArrowBlock {
  circular_arrow: SimpleSmartBlockItem[] | SimpleCircularArrowBlockConfig;
}

export interface SimpleColumnSmartBlockConfig extends SimpleSmartBlockBaseConfig {
  columns?: 'single' | 'two' | 'three';
  align?: SimpleAlignment;
}

export interface SimpleIconTextBlockConfig extends SimpleColumnSmartBlockConfig {
  orientation?: 'vertical' | 'horizontal';
}

/** Icon text smart block */
export interface SimpleIconTextBlock {
  icon_text: SimpleSmartBlockItem[] | SimpleIconTextBlockConfig;
}

/** Top circle boxes smart block */
export interface SimpleTopCircleBoxesBlock {
  top_circle_boxes: SimpleSmartBlockItem[] | SimpleColumnSmartBlockConfig;
}

/** Solid boxes smart block */
export interface SimpleSolidBoxesBlock {
  solid_boxes: SimpleSmartBlockItem[] | SimpleColumnSmartBlockConfig;
}

/** Side line boxes smart block */
export interface SimpleSideLineBoxesBlock {
  side_line_boxes: SimpleSmartBlockItem[] | SimpleColumnSmartBlockConfig;
}

/** Quote boxes smart block */
export interface SimpleQuoteBoxesBlock {
  quote_boxes: SimpleSmartBlockItem[] | SimpleColumnSmartBlockConfig;
}

// ============================================================================
// CONTENT BLOCKS
// ============================================================================

/**
 * Content blocks pull data from organization profile.
 * AI just specifies which type to include - data comes from elsewhere.
 */
export type SimpleContentBlockType =
  | 'services'
  | 'team'
  | 'testimonials'
  | 'portfolio'
  | 'case_studies'
  | 'technologies'
  | 'industries'
  | 'pricing_packages'
  | 'products'
  | 'projects'
  | 'awards_recognition'
  | 'faqs'
  | 'terms'
  | 'legal_compliance'
  | 'methodology'
  | 'key_metrics'
  | 'about_us'
  | 'why_work_with_us'
  | 'social_links';

/** Content block layout options */
export type SimpleContentBlockLayout =
  | 'grid'
  | 'cards'
  | 'list'
  | 'card'
  | 'icon_text'
  | 'icon_only'
  | 'detailed'
  | 'table'
  | 'featured'
  | 'carousel'
  | 'showcase'
  | 'image-left'
  | 'image-right'
  | 'centered';

/** Content block options */
export interface SimpleContentBlockOptions {
  layout?: SimpleContentBlockLayout;
  /** UUIDs of content items to display (resolved from knowledge base at load time) */
  items?: string[];
}

/** Content block - can be true (default layout) or object with layout option */
export type SimpleContentBlock = {
  [K in SimpleContentBlockType]?: true | SimpleContentBlockOptions;
};

// ============================================================================
// LAYOUT
// ============================================================================

/** Multi-column row */
export interface SimpleRowBlock {
  columns: SimpleItem[][];
  widths?: number[]; // Percentage widths, should sum to 100
}

// ============================================================================
// UTILITY BLOCKS
// ============================================================================

/** Vertical space */
export interface SimpleSpaceBlock {
  space: number; // Height in pixels
}

/** Page break */
export interface SimplePageBreakBlock {
  page_break: true;
}

/** Table of contents */
export interface SimpleTocBlock {
  table_of_contents:
    | true
    | {
        include_heading_levels?: Partial<
          Record<
            'heading-one' | 'heading-two' | 'heading-three' | 'heading-four' | 'heading-five',
            boolean
          >
        >;
        show_page_numbers?: boolean;
        page_number_alignment?: 'left' | 'right';
        style?: SimpleCommonBlockStyle;
      };
}

// ============================================================================
// FILLABLE BLOCKS (document flow, not absolute positioning)
// ============================================================================

/**
 * Editor Signature block - for the editor/sender to sign
 */
export interface SimpleEditorSignatureBlock {
  editor_signature_block:
    | true
    | {
        placeholder?: string;
        text_placeholder?: string;
        align?: SimpleAlignment;
      };
}

/**
 * Recipient Signature block - for capturing signatures from recipient
 * Viewers can sign using draw, type, or upload
 */
export interface SimpleRecipientSignatureBlock {
  recipient_signature_block:
    | true
    | {
        placeholder?: string;
        text_placeholder?: string;
        align?: SimpleAlignment;
      };
}

/**
 * Recipient Initials block - for capturing initials from recipient
 * Viewers can add initials using draw or type
 */
export interface SimpleRecipientInitialsBlock {
  recipient_initials_block:
    | true
    | {
        placeholder?: string;
        align?: SimpleAlignment;
      };
}

/**
 * Recipient Stamp block - for adding stamps/watermarks from recipient
 * Viewers can upload stamp images
 */
export interface SimpleRecipientStampBlock {
  recipient_stamp_block:
    | true
    | {
        placeholder?: string;
        align?: SimpleAlignment;
      };
}

/**
 * Editor Initials block - for capturing initials from editor/sender
 */
export interface SimpleEditorInitialsBlock {
  editor_initials_block:
    | true
    | {
        placeholder?: string;
        align?: SimpleAlignment;
      };
}

/**
 * Editor Stamp block - for adding stamps/watermarks from editor/sender
 */
export interface SimpleEditorStampBlock {
  editor_stamp_block:
    | true
    | {
        placeholder?: string;
        align?: SimpleAlignment;
      };
}

/**
 * Editor Fillable text block - with inline form fields for editor/sender
 */
export interface SimpleEditorFillableTextBlock {
  editor_fillable_text:
    | SimpleFillableTextContent
    | {
        content: SimpleFillableChildItem[];
        style?: SimpleTextStyle;
      };
}

/**
 * Fillable text block with inline form fields
 * Uses structured rich text arrays with explicit { field: ... } inline nodes
 * Example: [{ text: 'I, ' }, { field: { type: 'name', placeholder: 'Your Name' } }, { text: ', agree to the terms on ' }, { field: { type: 'date', placeholder: 'Date' } }, { text: '.' }]
 *
 * Supported field types: text_input, name, email, date, number, checkbox, dropdown
 */
export interface SimpleFillableTextBlock {
  fillable_text:
    | SimpleFillableTextContent
    | {
        content: SimpleFillableChildItem[];
        style?: SimpleTextStyle;
      };
}

// ============================================================================
// COMBINED TYPES
// ============================================================================

/** Any simple block/item */
export type SimpleItem =
  | SimpleTextBlock
  | SimpleTextGroupBlock
  | SimpleImageBlock
  | SimpleVideoBlock
  | SimpleEmbedWebpageBlock
  | SimpleButtonBlock
  | SimpleIconBlock
  | SimpleCalloutBlock
  | SimpleDividerBlock
  | SimpleLabelBlock
  | SimpleBlockQuoteBlock
  | SimpleCodeBlock
  | SimpleChartBlock
  | SimpleTableBlock
  | SimplePricingTableBlock
  | SimpleQuotationBuilderBlock
  | SimpleTimelineBlock
  | SimpleProcessFlowBlock
  | SimpleStepsBlock
  | SimpleArrowBlock
  | SimpleStaircaseBlock
  | SimplePyramidBlock
  | SimpleFunnelBlock
  | SimpleCircularArrowBlock
  | SimpleIconTextBlock
  | SimpleTopCircleBoxesBlock
  | SimpleSolidBoxesBlock
  | SimpleSideLineBoxesBlock
  | SimpleQuoteBoxesBlock
  | SimpleContentBlock
  | SimpleRowBlock
  | SimpleSpaceBlock
  | SimplePageBreakBlock
  | SimpleTocBlock
  | SimpleEditorSignatureBlock
  | SimpleRecipientSignatureBlock
  | SimpleRecipientInitialsBlock
  | SimpleRecipientStampBlock
  | SimpleEditorInitialsBlock
  | SimpleEditorStampBlock
  | SimpleEditorFillableTextBlock
  | SimpleFillableTextBlock;

// ============================================================================
// PAGE
// ============================================================================

export type SimplePageLayoutMode = 'left' | 'right' | 'top' | 'background';

export type SimpleLayoutImage =
  | string
  | {
      url?: string;
      prompt?: string;
      ref?: string; // Reference to a provided source image, resolved to a URL at build time.
      image_generation_request_id?: string;
      image_generation_status?: string;
      image_generation_error?: string;
    };

export interface SimplePageCardAppearance {
  mode: 'theme' | 'solid' | 'gradient';
  value?: string;
}

export interface SimplePageLayoutFillAppearance {
  mode: 'solid' | 'gradient';
  value: string;
}

export interface SimplePageLayoutMediaAppearance {
  /** Omitted for images. A video or embed background is drawn with a player, not an img. */
  type?: 'video' | 'embed';
  image?: SimpleLayoutImage;
  fill?: SimplePageLayoutFillAppearance;
  fit?: 'contain' | 'cover';
  focus?: {
    x: number;
    y: number;
  };
}

export interface SimplePageLayoutOverlayAppearance {
  effect: 'frosted' | 'faded' | 'clear';
  intensity?: number;
  /** Blur strength; the builder uses `intensity` when it is not set. */
  blur?: number;
  color?: string;
}

export interface SimplePageLayoutAppearance {
  mode: SimplePageLayoutMode;
  size?: {
    width?: number;
    height?: number;
  };
  media?: SimplePageLayoutMediaAppearance;
  overlay?: SimplePageLayoutOverlayAppearance;
}

export interface SimplePageAppearance {
  visibility?: boolean;
  card?: SimplePageCardAppearance;
  layout?: SimplePageLayoutAppearance;
}

export type SimplePageHeaderFooterSlot =
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right';

export type SimplePageHeaderFooterElementType = 'text' | 'number' | 'theme_logo' | 'image';

export interface SimplePageHeaderFooterElement {
  id?: string;
  slot: SimplePageHeaderFooterSlot;
  type: SimplePageHeaderFooterElementType;
  value?: string;
  imageUrl?: string;
  enabled?: boolean;
  width?: number;
  height?: number;
}

export interface SimplePageHeaderFooterPreserve {
  hideForThisSection?: boolean;
  override?: {
    elements?: SimplePageHeaderFooterElement[];
  };
}

export interface SimplePagePreserve {
  header_footer?: SimplePageHeaderFooterPreserve;
}

/** Simple page structure */
export interface SimplePage {
  name: string;
  /**
   * Page-level landscape override. Honoured only for the document layout. `true` forces this page
   * landscape; `false` clears the override so the page follows the document's own orientation.
   */
  isLandscape?: boolean;
  appearance?: SimplePageAppearance;
  _preserve?: SimplePagePreserve;
  content: SimpleItem[];
}

/** Complete simple document */
export type SimpleDocument = SimplePage[];

// ============================================================================
// TYPE GUARDS
// ============================================================================

export function isSimpleTextBlock(item: SimpleItem): item is SimpleTextBlock {
  return (
    'h1' in item ||
    'h2' in item ||
    'h3' in item ||
    'h4' in item ||
    'h5' in item ||
    'p' in item ||
    'bullets' in item ||
    'numbered' in item ||
    'todo' in item
  );
}

export function isSimpleTextGroupBlock(item: SimpleItem): item is SimpleTextGroupBlock {
  return 'text' in item;
}

export function isSimpleImageBlock(item: SimpleItem): item is SimpleImageBlock {
  return 'image' in item;
}

export function isSimpleVideoBlock(item: SimpleItem): item is SimpleVideoBlock {
  return 'video' in item;
}

export function isSimpleEmbedWebpageBlock(item: SimpleItem): item is SimpleEmbedWebpageBlock {
  return 'embed_webpage' in item;
}

export function isSimpleButtonBlock(item: SimpleItem): item is SimpleButtonBlock {
  return 'button' in item;
}

export function isSimpleIconBlock(item: SimpleItem): item is SimpleIconBlock {
  return 'icon' in item;
}

export function isSimpleCalloutBlock(item: SimpleItem): item is SimpleCalloutBlock {
  return 'callout_box' in item;
}

export function isSimpleDividerBlock(item: SimpleItem): item is SimpleDividerBlock {
  return 'divider' in item;
}

export function isSimpleLabelBlock(item: SimpleItem): item is SimpleLabelBlock {
  return 'label' in item;
}

export function isSimpleBlockQuoteBlock(item: SimpleItem): item is SimpleBlockQuoteBlock {
  return 'block_quote' in item;
}

export function isSimpleCodeBlock(item: SimpleItem): item is SimpleCodeBlock {
  return 'code' in item;
}

export function isSimpleChartBlock(item: SimpleItem): item is SimpleChartBlock {
  return 'chart' in item;
}

export function isSimpleTableBlock(item: SimpleItem): item is SimpleTableBlock {
  return 'table' in item;
}

export function isSimpleRowBlock(item: SimpleItem): item is SimpleRowBlock {
  return 'columns' in item;
}

export function isSimpleSpaceBlock(item: SimpleItem): item is SimpleSpaceBlock {
  return 'space' in item;
}

export function isSimplePageBreakBlock(item: SimpleItem): item is SimplePageBreakBlock {
  return 'page_break' in item;
}

export function isSimpleTocBlock(item: SimpleItem): item is SimpleTocBlock {
  return 'table_of_contents' in item;
}

export function isSimplePricingTableBlock(item: SimpleItem): item is SimplePricingTableBlock {
  return 'pricing_table' in item;
}

export function isSimpleQuotationBuilderBlock(
  item: SimpleItem,
): item is SimpleQuotationBuilderBlock {
  return 'quotation_builder' in item;
}

// Smart block type guards
export function isSimpleTimelineBlock(item: SimpleItem): item is SimpleTimelineBlock {
  return 'timeline' in item;
}

export function isSimpleProcessFlowBlock(item: SimpleItem): item is SimpleProcessFlowBlock {
  return 'process_flow' in item;
}

export function isSimpleStepsBlock(item: SimpleItem): item is SimpleStepsBlock {
  return 'steps' in item;
}

export function isSimpleArrowBlock(item: SimpleItem): item is SimpleArrowBlock {
  return 'arrow' in item;
}

export function isSimpleStaircaseBlock(item: SimpleItem): item is SimpleStaircaseBlock {
  return 'staircase' in item;
}

export function isSimplePyramidBlock(item: SimpleItem): item is SimplePyramidBlock {
  return 'pyramid' in item;
}

export function isSimpleFunnelBlock(item: SimpleItem): item is SimpleFunnelBlock {
  return 'funnel' in item;
}

export function isSimpleCircularArrowBlock(item: SimpleItem): item is SimpleCircularArrowBlock {
  return 'circular_arrow' in item;
}

export function isSimpleIconTextBlock(item: SimpleItem): item is SimpleIconTextBlock {
  return 'icon_text' in item;
}

export function isSimpleTopCircleBoxesBlock(item: SimpleItem): item is SimpleTopCircleBoxesBlock {
  return 'top_circle_boxes' in item;
}

export function isSimpleSolidBoxesBlock(item: SimpleItem): item is SimpleSolidBoxesBlock {
  return 'solid_boxes' in item;
}

export function isSimpleSideLineBoxesBlock(item: SimpleItem): item is SimpleSideLineBoxesBlock {
  return 'side_line_boxes' in item;
}

export function isSimpleQuoteBoxesBlock(item: SimpleItem): item is SimpleQuoteBoxesBlock {
  return 'quote_boxes' in item;
}

// Content block detection
const CONTENT_BLOCK_TYPES: SimpleContentBlockType[] = [
  'services',
  'team',
  'testimonials',
  'portfolio',
  'case_studies',
  'technologies',
  'industries',
  'pricing_packages',
  'products',
  'projects',
  'awards_recognition',
  'faqs',
  'terms',
  'legal_compliance',
  'methodology',
  'key_metrics',
  'about_us',
  'why_work_with_us',
  'social_links',
];

export function isSimpleContentBlock(item: SimpleItem): item is SimpleContentBlock {
  return CONTENT_BLOCK_TYPES.some((type) => type in item);
}

export function getContentBlockType(item: SimpleContentBlock): SimpleContentBlockType | null {
  for (const type of CONTENT_BLOCK_TYPES) {
    if (type in item) return type;
  }
  return null;
}

// Smart block detection
const SMART_BLOCK_KEYS = [
  'timeline',
  'process_flow',
  'steps',
  'arrow',
  'staircase',
  'pyramid',
  'funnel',
  'circular_arrow',
  'icon_text',
  'top_circle_boxes',
  'solid_boxes',
  'side_line_boxes',
  'quote_boxes',
];

export function isSimpleSmartBlock(item: SimpleItem): boolean {
  return SMART_BLOCK_KEYS.some((key) => key in item);
}

// ============================================================================
// FILLABLE BLOCK TYPE GUARDS
// ============================================================================

export function isSimpleEditorSignatureBlock(item: SimpleItem): item is SimpleEditorSignatureBlock {
  return 'editor_signature_block' in item;
}

export function isSimpleRecipientSignatureBlock(
  item: SimpleItem,
): item is SimpleRecipientSignatureBlock {
  return 'recipient_signature_block' in item;
}

export function isSimpleRecipientInitialsBlock(
  item: SimpleItem,
): item is SimpleRecipientInitialsBlock {
  return 'recipient_initials_block' in item;
}

export function isSimpleRecipientStampBlock(item: SimpleItem): item is SimpleRecipientStampBlock {
  return 'recipient_stamp_block' in item;
}

export function isSimpleEditorInitialsBlock(item: SimpleItem): item is SimpleEditorInitialsBlock {
  return 'editor_initials_block' in item;
}

export function isSimpleEditorStampBlock(item: SimpleItem): item is SimpleEditorStampBlock {
  return 'editor_stamp_block' in item;
}

export function isSimpleEditorFillableTextBlock(
  item: SimpleItem,
): item is SimpleEditorFillableTextBlock {
  return 'editor_fillable_text' in item;
}

export function isSimpleFillableTextBlock(item: SimpleItem): item is SimpleFillableTextBlock {
  return 'fillable_text' in item;
}
